import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Plus, Trash2, Info, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/context/LanguageContext";
import { tokenStore } from "@/lib/api";
import { toast } from "sonner";

const ENTROPY_SOURCES = [
  { id: "raw_qrng", label: "Raw QRNG", desc: "Direct quantum entropy, no post-processing" },
  { id: "qrng_drbg", label: "QRNG + DRBG", desc: "Quantum-seeded deterministic expansion (AES-CTR/ChaCha20)" },
  { id: "vrf", label: "VRF Service", desc: "Verifiable Random Function output with proof" },
];

const COMPLIANCE_OPTIONS = [
  { id: "nist_sp800_90a", label: "NIST SP 800-90A" },
  { id: "nist_sp800_90b", label: "NIST SP 800-90B" },
  { id: "fips_140_2", label: "FIPS 140-2" },
  { id: "fips_140_3", label: "FIPS 140-3" },
  { id: "iso_27001", label: "ISO 27001" },
  { id: "pci_dss", label: "PCI DSS" },
  { id: "hipaa", label: "HIPAA" },
];

const INDUSTRY_OPTIONS = [
  { value: "technology", key: "industry.technology" },
  { value: "finance", key: "industry.finance" },
  { value: "healthcare", key: "industry.healthcare" },
  { value: "government", key: "industry.government" },
  { value: "defense", key: "industry.defense" },
  { value: "telecommunications", key: "industry.telecommunications" },
  { value: "energy", key: "industry.energy" },
  { value: "manufacturing", key: "industry.manufacturing" },
  { value: "retail", key: "industry.retail" },
  { value: "education", key: "industry.education" },
  { value: "media", key: "industry.media" },
  { value: "gaming", key: "industry.gaming" },
  { value: "automotive", key: "industry.automotive" },
  { value: "research", key: "industry.research" },
  { value: "other", key: "industry.other" },
];

const COMPANY_SIZE_OPTIONS = [
  { value: "1-10", key: "company_size.1-10" },
  { value: "11-50", key: "company_size.11-50" },
  { value: "51-200", key: "company_size.51-200" },
  { value: "201-500", key: "company_size.201-500" },
  { value: "501-1000", key: "company_size.501-1000" },
  { value: "1001-5000", key: "company_size.1001-5000" },
  { value: "5000+", key: "company_size.5000+" },
];

const ORG_TYPE_OPTIONS = [
  { value: "enterprise", key: "org_type.enterprise" },
  { value: "startup", key: "org_type.startup" },
  { value: "smb", key: "org_type.smb" },
  { value: "government", key: "org_type.government" },
  { value: "nonprofit", key: "org_type.nonprofit" },
  { value: "educational", key: "org_type.educational" },
];

export default function TenantOnboard() {
  const [ipEntries, setIpEntries] = useState([""]);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [dedicatedPool, setDedicatedPool] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  // Entropy config state
  const [entropySources, setEntropySources] = useState(["raw_qrng", "qrng_drbg"]);
  const [drbgAlgorithm, setDrbgAlgorithm] = useState("AES-256-CTR");
  const [reseedInterval, setReseedInterval] = useState(10000);
  const [expansionFactor, setExpansionFactor] = useState(100);
  const [poolSizeMb, setPoolSizeMb] = useState(64);
  const [outputFormats, setOutputFormats] = useState(["hex", "base64", "binary", "integer"]);
  const { t } = useLanguage();
  const navigate = useNavigate();

  const toggleEntropySource = (id) => {
    setEntropySources(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const toggleOutputFormat = (fmt) => {
    setOutputFormats(prev =>
      prev.includes(fmt) ? prev.filter(f => f !== fmt) : [...prev, fmt]
    );
  };

  const algoMap = { "aes-ctr": "AES-256-CTR", "chacha20": "ChaCha20", "hmac": "HMAC-SHA256" };
  const factorMap = { "10x": 10, "100x": 100, "1000x": 1000 };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/plans/`, {
          headers: { Authorization: `Bearer ${tokenStore.getAccess()}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPlans(data.filter(p => p.is_active));
          const recommended = data.find(p => p.slug === "business") || data[0];
          if (recommended) setSelectedPlanId(recommended.id);
        }
      } catch { /* use empty */ }
      finally { setPlansLoading(false); }
    })();
  }, []);

  const addIp = () => setIpEntries([...ipEntries, ""]);
  const removeIp = (i) => setIpEntries(ipEntries.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!orgName.trim() || !contactEmail.trim() || !selectedPlanId) {
      toast.error("Please fill in all required fields and select a plan");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/tenants/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenStore.getAccess()}`
        },
        body: JSON.stringify({
          name: orgName,
          owner_email: contactEmail,
          plan_id: selectedPlanId,
          entropy_sources: entropySources,
          drbg_algorithm: drbgAlgorithm,
          drbg_expansion_factor: expansionFactor,
          drbg_reseed_interval: Number(reseedInterval),
          dedicated_entropy_pool: dedicatedPool,
          dedicated_pool_size_mb: Number(poolSizeMb),
          allowed_output_formats: outputFormats,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.error?.message || "Failed to create tenant");
      }
      toast.success("Tenant onboarded successfully");
      navigate("/admin/tenants");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TooltipProvider>
      <div data-testid="tenant-onboard-page">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin/tenants" className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" data-testid="back-to-tenants-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="tenant-onboard-title">Onboard New Tenant</h1>
            <p className="text-sm text-slate-500 mt-0.5">Configure organization, API access, rate limits, entropy preferences, and compliance</p>
          </div>
        </div>

        <Tabs defaultValue="organization" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 h-10 p-0.5 w-auto" data-testid="onboard-tabs">
            <TabsTrigger value="organization" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="onboard-tab-org">1. Organization</TabsTrigger>
            <TabsTrigger value="plan" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="onboard-tab-plan">2. Plan & Limits</TabsTrigger>
            <TabsTrigger value="entropy" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="onboard-tab-entropy">3. Entropy Config</TabsTrigger>
            <TabsTrigger value="security" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="onboard-tab-security">4. Security & Compliance</TabsTrigger>
            <TabsTrigger value="notifications" className="text-xs px-4 data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="onboard-tab-notif">5. Notifications</TabsTrigger>
          </TabsList>

          {/* Step 1: Organization */}
          <TabsContent value="organization">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 p-6" data-testid="org-details-section">
                <h3 className="text-sm font-semibold text-slate-900 mb-5">Organization Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t("tenant.org_name")} *</Label>
                    <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="e.g., Acme Corporation" className="mt-1.5 h-10 bg-white" data-testid="onboard-org-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Legal Entity Name</Label>
                    <Input placeholder="e.g., Acme Corp, Inc." className="mt-1.5 h-10 bg-white" data-testid="onboard-legal-name" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t("tenant.industry")} *</Label>
                    <Select>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-industry"><SelectValue placeholder={t("tenant.industry")} /></SelectTrigger>
                      <SelectContent>
                        {INDUSTRY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{t(opt.key)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t("tenant.company_size")}</Label>
                    <Select>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-company-size"><SelectValue placeholder={t("tenant.company_size")} /></SelectTrigger>
                      <SelectContent>
                        {COMPANY_SIZE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{t(opt.key)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Country / Region *</Label>
                    <Select>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in">India</SelectItem>
                        <SelectItem value="us">United States</SelectItem>
                        <SelectItem value="uk">United Kingdom</SelectItem>
                        <SelectItem value="sg">Singapore</SelectItem>
                        <SelectItem value="ae">UAE</SelectItem>
                        <SelectItem value="de">Germany</SelectItem>
                        <SelectItem value="jp">Japan</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Data Residency Preference</Label>
                    <Select>
                      <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-residency"><SelectValue placeholder="Select region" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ap-south-1">Asia Pacific (Mumbai)</SelectItem>
                        <SelectItem value="us-east-1">US East (Virginia)</SelectItem>
                        <SelectItem value="eu-west-1">Europe (Ireland)</SelectItem>
                        <SelectItem value="ap-southeast-1">Asia Pacific (Singapore)</SelectItem>
                        <SelectItem value="no-preference">No Preference</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-slate-200 p-6" data-testid="primary-contact-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-5">Primary Contact</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">First Name *</Label>
                        <Input placeholder="Jane" className="mt-1.5 h-10 bg-white" data-testid="onboard-contact-first" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Last Name *</Label>
                        <Input placeholder="Doe" className="mt-1.5 h-10 bg-white" data-testid="onboard-contact-last" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Email *</Label>
                      <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="jane@acme.com" className="mt-1.5 h-10 bg-white" data-testid="onboard-contact-email" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Phone</Label>
                      <Input type="tel" placeholder="+91 98765 43210" className="mt-1.5 h-10 bg-white" data-testid="onboard-contact-phone" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Job Title</Label>
                      <Input placeholder="e.g., VP Engineering" className="mt-1.5 h-10 bg-white" data-testid="onboard-contact-title" />
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 p-6" data-testid="billing-contact-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-5">Billing Contact</h3>
                  <div className="flex items-center gap-2 mb-4">
                    <Checkbox id="same-as-primary" data-testid="same-as-primary-checkbox" />
                    <Label htmlFor="same-as-primary" className="text-sm text-slate-600 cursor-pointer">Same as primary contact</Label>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Billing Email</Label>
                      <Input type="email" placeholder="billing@acme.com" className="mt-1.5 h-10 bg-white" data-testid="onboard-billing-email" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Billing Address</Label>
                      <Input placeholder="Street address, City, State, ZIP" className="mt-1.5 h-10 bg-white" data-testid="onboard-billing-address" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Tax ID / GST Number</Label>
                      <Input placeholder="e.g., GSTIN" className="mt-1.5 h-10 bg-white" data-testid="onboard-tax-id" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Step 2: Plan & Limits */}
          <TabsContent value="plan">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 p-6" data-testid="plan-selection-section">
                <h3 className="text-sm font-semibold text-slate-900 mb-5">Plan Selection</h3>
                <div className="space-y-3">
                  {plansLoading ? (
                    <div className="flex items-center justify-center py-6 text-slate-400 gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading plans...</div>
                  ) : plans.map((plan) => (
                    <label key={plan.id} className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors hover:bg-slate-50 ${selectedPlanId === plan.id ? 'border-cyan-300 bg-cyan-50/30' : 'border-slate-200'}`} data-testid={`plan-radio-${plan.slug}`}>
                      <input type="radio" name="plan" value={plan.id} className="mt-1 accent-cyan-600" checked={selectedPlanId === plan.id} onChange={() => setSelectedPlanId(plan.id)} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{plan.name}</span>
                          <span className="text-sm font-bold text-slate-700">
                            {plan.price_monthly === 0 && plan.slug !== "free" ? "Custom" : plan.slug === "free" ? "Free" : `$${plan.price_monthly}/mo`}
                          </span>
                          {plan.badge && <Badge className="bg-cyan-100 text-cyan-700 text-[9px] border-0">{plan.badge}</Badge>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{plan.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-slate-200 p-6" data-testid="rate-limits-section">
                  <div className="flex items-center gap-2 mb-5">
                    <h3 className="text-sm font-semibold text-slate-900">Rate Limits & Quotas</h3>
                    <Tooltip><TooltipTrigger><Info className="w-3.5 h-3.5 text-slate-400" /></TooltipTrigger><TooltipContent><p className="text-xs">Override plan defaults with custom limits</p></TooltipContent></Tooltip>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Max API Calls / Month</Label>
                      <Input type="number" defaultValue="1000000" className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-max-calls" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Max Requests / Second</Label>
                      <Input type="number" defaultValue="100" className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-max-rps" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Max Requests / Minute</Label>
                      <Input type="number" defaultValue="1000" className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-max-rpm" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Max Bandwidth / Month (GB)</Label>
                      <Input type="number" defaultValue="50" className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-max-bandwidth" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Max Bytes Per Request</Label>
                      <Input type="number" defaultValue="1048576" className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-max-bytes" />
                      <p className="text-[10px] text-slate-400 mt-1">Default: 1 MB (1,048,576 bytes)</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Max API Keys</Label>
                      <Input type="number" defaultValue="10" className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-max-keys" />
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 p-6" data-testid="sla-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-5">SLA Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">SLA Tier</Label>
                      <Select defaultValue="standard">
                        <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-sla-tier"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="basic">Basic (99.5% uptime)</SelectItem>
                          <SelectItem value="standard">Standard (99.9% uptime)</SelectItem>
                          <SelectItem value="premium">Premium (99.95% uptime)</SelectItem>
                          <SelectItem value="mission-critical">Mission-Critical (99.99% uptime)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Support Tier</Label>
                      <Select defaultValue="priority">
                        <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-support-tier"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="community">Community (48h response)</SelectItem>
                          <SelectItem value="priority">Priority (4h response)</SelectItem>
                          <SelectItem value="dedicated">Dedicated Engineer (1h response)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Step 3: Entropy Config */}
          <TabsContent value="entropy">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 p-6" data-testid="entropy-sources-section">
                <h3 className="text-sm font-semibold text-slate-900 mb-5">Entropy Source Access</h3>
                <p className="text-xs text-slate-500 mb-4">Select which entropy sources this tenant can access via API</p>
                <div className="space-y-3">
                  {ENTROPY_SOURCES.map((src) => (
                    <label key={src.id} className="flex items-start gap-3 p-4 border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`entropy-source-${src.id}`}>
                      <Checkbox
                        id={src.id}
                        checked={entropySources.includes(src.id)}
                        onCheckedChange={() => toggleEntropySource(src.id)}
                        className="mt-0.5"
                      />
                      <div>
                        <div className="text-sm font-medium text-slate-900">{src.label}</div>
                        <p className="text-xs text-slate-500 mt-0.5">{src.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-slate-200 p-6" data-testid="drbg-config-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-5">DRBG Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">DRBG Algorithm</Label>
                      <Select value={Object.keys(algoMap).find(k => algoMap[k] === drbgAlgorithm) || "aes-ctr"} onValueChange={(v) => setDrbgAlgorithm(algoMap[v] || v)}>
                        <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-drbg-algo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="aes-ctr">AES-256-CTR DRBG</SelectItem>
                          <SelectItem value="chacha20">ChaCha20-Poly1305 DRBG</SelectItem>
                          <SelectItem value="hmac">HMAC-DRBG (SHA-256)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Reseed Interval (requests)</Label>
                      <Input type="number" value={reseedInterval} onChange={(e) => setReseedInterval(e.target.value)} className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-reseed-interval" />
                      <p className="text-[10px] text-slate-400 mt-1">DRBG will be reseeded with fresh QRNG entropy after this many requests</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Expansion Factor</Label>
                      <Select value={Object.keys(factorMap).find(k => factorMap[k] === expansionFactor) || "100x"} onValueChange={(v) => setExpansionFactor(factorMap[v] || 100)}>
                        <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-expansion"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10x">10x (Higher entropy quality)</SelectItem>
                          <SelectItem value="100x">100x (Balanced)</SelectItem>
                          <SelectItem value="1000x">1000x (Maximum throughput)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 p-6" data-testid="dedicated-pool-section">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Dedicated Entropy Pool</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Allocate a dedicated QRNG entropy buffer for this tenant (Enterprise only)</p>
                    </div>
                    <Switch checked={dedicatedPool} onCheckedChange={setDedicatedPool} data-testid="dedicated-pool-toggle" />
                  </div>
                  {dedicatedPool && (
                    <div className="space-y-4 pt-2 border-t border-slate-100">
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium mt-4 block">Pool Size (MB)</Label>
                        <Input type="number" value={poolSizeMb} onChange={(e) => setPoolSizeMb(e.target.value)} className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-pool-size" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Min Fill Level (%)</Label>
                        <Input type="number" defaultValue="30" className="mt-1.5 h-10 bg-white font-mono" data-testid="onboard-pool-min" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Assigned QRNG Device</Label>
                        <Select>
                          <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-assigned-device"><SelectValue placeholder="Auto-assign" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Auto-assign (load balanced)</SelectItem>
                            <SelectItem value="qrng-01">QRNG-PROD-01 (DC-Mumbai-1)</SelectItem>
                            <SelectItem value="qrng-02">QRNG-PROD-02 (DC-Mumbai-1)</SelectItem>
                            <SelectItem value="qrng-03">QRNG-PROD-03 (DC-Mumbai-2)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-white border border-slate-200 p-6" data-testid="output-formats-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-5">Allowed Output Formats</h3>
                  <div className="space-y-2">
                    {["hex", "base64", "binary", "integer", "uuid"].map((fmt) => (
                      <label key={fmt} className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer transition-colors">
                        <Checkbox
                          id={`fmt-${fmt}`}
                          checked={outputFormats.includes(fmt)}
                          onCheckedChange={() => toggleOutputFormat(fmt)}
                          data-testid={`format-${fmt}`}
                        />
                        <Label htmlFor={`fmt-${fmt}`} className="text-sm text-slate-700 cursor-pointer font-mono">{fmt}</Label>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Step 4: Security & Compliance */}
          <TabsContent value="security">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 p-6" data-testid="ip-whitelist-section">
                <h3 className="text-sm font-semibold text-slate-900 mb-5">IP Whitelist</h3>
                <p className="text-xs text-slate-500 mb-4">Restrict API access to specific IP addresses or CIDR ranges. Leave empty to allow all.</p>
                <div className="space-y-2">
                  {ipEntries.map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input placeholder="e.g., 192.168.1.0/24" className="h-9 bg-white font-mono text-sm" data-testid={`ip-entry-${i}`} />
                      {ipEntries.length > 1 && (
                        <button onClick={() => removeIp(i)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`remove-ip-${i}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button onClick={addIp} className="flex items-center gap-1.5 text-xs text-cyan-600 hover:text-cyan-700 font-medium mt-2" data-testid="add-ip-btn">
                    <Plus className="w-3.5 h-3.5" /> Add IP / CIDR
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-slate-200 p-6" data-testid="auth-config-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-5">Authentication Configuration</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Auth Method</Label>
                      <Select defaultValue="bearer">
                        <SelectTrigger className="mt-1.5 h-10" data-testid="onboard-auth-method"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bearer">Bearer Token (API Key)</SelectItem>
                          <SelectItem value="mtls">Mutual TLS (mTLS)</SelectItem>
                          <SelectItem value="both">Bearer + mTLS (Dual Auth)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between p-3 border border-slate-100">
                      <div>
                        <div className="text-sm text-slate-900">Enforce MFA for Portal Access</div>
                        <div className="text-xs text-slate-500">Require TOTP-based MFA for all team members</div>
                      </div>
                      <Switch defaultChecked data-testid="enforce-mfa-toggle" />
                    </div>
                    <div className="flex items-center justify-between p-3 border border-slate-100">
                      <div>
                        <div className="text-sm text-slate-900">API Key Rotation Policy</div>
                        <div className="text-xs text-slate-500">Auto-expire API keys after period</div>
                      </div>
                      <Select defaultValue="90">
                        <SelectTrigger className="w-32 h-9 text-xs" data-testid="key-rotation-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="never">Never</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                          <SelectItem value="180">180 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="bg-white border border-slate-200 p-6" data-testid="compliance-section">
                  <h3 className="text-sm font-semibold text-slate-900 mb-5">Compliance Requirements</h3>
                  <p className="text-xs text-slate-500 mb-4">Select compliance frameworks applicable to this tenant</p>
                  <div className="space-y-2">
                    {COMPLIANCE_OPTIONS.map((opt) => (
                      <label key={opt.id} className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer transition-colors">
                        <Checkbox id={opt.id} defaultChecked={opt.id.startsWith("nist")} data-testid={`compliance-${opt.id}`} />
                        <Label htmlFor={opt.id} className="text-sm text-slate-700 cursor-pointer">{opt.label}</Label>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Step 5: Notifications */}
          <TabsContent value="notifications">
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-slate-200 p-6" data-testid="webhook-section">
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Webhook Configuration</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Send real-time event notifications to a webhook endpoint</p>
                  </div>
                  <Switch checked={webhookEnabled} onCheckedChange={setWebhookEnabled} data-testid="webhook-toggle" />
                </div>
                {webhookEnabled && (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Webhook URL</Label>
                      <Input placeholder="https://acme.com/webhooks/qcoresecure" className="mt-1.5 h-10 bg-white font-mono text-sm" data-testid="onboard-webhook-url" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Webhook Secret</Label>
                      <Input placeholder="Auto-generated on save" className="mt-1.5 h-10 bg-white font-mono text-sm" disabled data-testid="onboard-webhook-secret" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3 block">Events to Send</Label>
                      <div className="space-y-2">
                        {["usage.threshold_reached", "api_key.created", "api_key.revoked", "health.degraded", "billing.invoice_generated", "security.anomaly_detected"].map((evt) => (
                          <label key={evt} className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer transition-colors">
                            <Checkbox defaultChecked data-testid={`webhook-event-${evt}`} />
                            <span className="text-xs font-mono text-slate-700">{evt}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 p-6" data-testid="notification-prefs-section">
                <h3 className="text-sm font-semibold text-slate-900 mb-5">Email Notifications</h3>
                <div className="space-y-3">
                  {[
                    { label: "Usage threshold alerts (80%, 90%, 100%)", default: true },
                    { label: "Weekly usage summary report", default: true },
                    { label: "Monthly invoice notification", default: true },
                    { label: "API key expiration reminders", default: true },
                    { label: "Security incident notifications", default: true },
                    { label: "System maintenance announcements", default: true },
                    { label: "New feature announcements", default: false },
                  ].map((n, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border border-slate-100">
                      <span className="text-sm text-slate-700">{n.label}</span>
                      <Switch defaultChecked={n.default} data-testid={`notif-toggle-${i}`} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Bar */}
        <div className="sticky bottom-0 bg-[#F8FAFC] border-t border-slate-200 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 mt-8 flex items-center justify-between" data-testid="onboard-action-bar">
          <Link to="/admin/tenants" className="text-sm text-slate-500 hover:text-slate-700 transition-colors" data-testid="onboard-cancel-btn">Cancel</Link>
          <div className="flex items-center gap-3">
            <button className="inline-flex items-center gap-2 border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors text-xs px-5 py-2.5" data-testid="onboard-save-draft-btn">Save as Draft</button>
            <button onClick={handleSave} disabled={submitting} className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 transition-colors text-xs font-bold uppercase tracking-widest px-6 py-2.5 disabled:opacity-60" data-testid="onboard-create-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Create Tenant
            </button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
